import { Navigate, useLocation } from "react-router-dom";
import { LoadingCard } from "@/components/LoadingCard";
import { BackendUnavailableCard } from "@/components/BackendUnavailableCard";
import { ConnectionLostNotice } from "@/components/ConnectionLostNotice";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { resolveAuthGate } from "@/lib/auth/resolveAuthGate";
import { SUPER_HERO_CAMPAIGN_PATH, SUPER_HERO_ENTRY_PATH } from '@/lib/campaigns/superHeroCampaign';

interface AuthGateProps {
  children: React.ReactNode;
}

export const AuthGate = ({ children }: AuthGateProps) => {
  const { user, isLoading, isBackendUnavailable, sessionVerified, retrySession, recheckConnection } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const gate = resolveAuthGate({ isLoading, user, isBackendUnavailable, sessionVerified });

  if (gate === 'loading') {
    return <LoadingCard fullScreen message={t('common.checkingAuth')} />;
  }

  if (gate === 'unavailable') {
    return <BackendUnavailableCard onRetry={() => { void retrySession(); }} />;
  }

  if (gate === 'unauthenticated') {
    if (location.pathname === SUPER_HERO_CAMPAIGN_PATH) return <Navigate to={SUPER_HERO_ENTRY_PATH} replace />;
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  if (gate === 'reconnecting') {
    return (
      <>
        {children}
        <ConnectionLostNotice onRetry={recheckConnection} />
      </>
    );
  }

  return <>{children}</>;
};
